import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    Block as BlockIcon,
    CheckCircleOutline as EnableIcon,
    Delete as DeleteIcon,
    LockReset as ResetPasswordIcon,
} from '@mui/icons-material';
import userAdminService from '../../services/userAdminService';
import useCan from '../../hooks/useCan';
import BarraElenco from '../common/BarraElenco';
import FiltroPillole from '../common/FiltroPillole';
import iniziali from '../common/iniziali';
import NewUserDialog from './NewUserDialog';
import PasswordTemporaneaDialog from './PasswordTemporaneaDialog';
import { CONTENITORE_TABELLA, TABELLA_PAGINA, TESTATA_TABELLA } from '../common/stiliTabella';

/**
 * I gruppi di una concessione, ridotti a un'etichetta.
 *
 * Il ruolo da solo non dice piu' cosa una persona vede: dalla fase F3 la concessione e' la
 * terna (persona, ruolo, gruppo), e mostrare il solo ruolo racconta meta' del fatto. Il
 * gruppo sta *accanto* al ruolo, nella stessa pillola, e non su una riga sua: appartiene
 * alla stessa concessione, e separarli suggerirebbe due assegnazioni dove ce n'e' una.
 *
 * `id` nullo e' «ogni gruppo» (decisione D6), non un gruppo mancante: un amministratore
 * globale mostrato con l'elenco vuoto si leggerebbe come chi non amministra niente. Per la
 * stessa ragione «ogni gruppo» assorbe il resto — chi ce l'ha non e' ristretto, e altri nomi
 * accanto farebbero credere il contrario.
 */
export const etichettaGruppi = (gruppi) => {
    if (!gruppi || gruppi.length === 0) {
        return 'nessun gruppo';
    }
    if (gruppi.some((g) => !g.id)) {
        return 'ogni gruppo';
    }
    return gruppi.map((g) => g.label || g.code || g.id).join(', ');
};

const DESCRIZIONE_TAB =
    'Gli utenti arrivano da Keycloak. Qui si decide quali ruoli hanno dentro Automate e, con ' +
    'i permessi necessari, si creano, si disattivano e si eliminano gli account del realm.';

// Il ritardo non e' un dettaglio implementativo: chi disattiva si aspetta che la persona sia
// fuori subito, e un'attesa non dichiarata viene letta come un guasto.
const NOTA_SESSIONI =
    'Le sessioni aperte vengono chiuse, ma un token già emesso può restare valido fino a ' +
    'cinque minuti.';

/**
 * Lo stato dell'account, in tre valori e non due.
 *
 * «Disattivato» e «senza account» arrivano entrambi come `enabled: false`, ma si rimediano
 * in modi opposti: il primo si riattiva da qui, il secondo no — la persona non esiste piu'
 * sul realm e restano solo le sue assegnazioni locali, da ripulire.
 */
function statoDi(user) {
    if (user.accountPresent === false) {
        return { etichetta: 'Senza account', chiave: 'assente' };
    }
    return user.enabled
        ? { etichetta: 'Attivo', chiave: 'attivo' }
        : { etichetta: 'Disattivato', chiave: 'disattivato' };
}

// «Senza account» non e' uno stato di Keycloak: sono le persone cancellate dal realm che
// hanno ancora concessioni qui. Con l'elenco a pagine non si riconoscono piu' scorrendolo,
// e servono solo a chi fa pulizia: hanno un filtro loro.
const FILTRI_STATO = [
    { valore: 'tutti', etichetta: 'Tutti' },
    { valore: 'attivi', etichetta: 'Attivi' },
    { valore: 'disattivati', etichetta: 'Disattivati' },
    { valore: 'senza-account', etichetta: 'Senza account' },
];

const DIMENSIONE_PAGINA_DEFAULT = 25;

// Il tetto imposto dal backend (UserAdminController.DIMENSIONE_MASSIMA).
const OPZIONI_DIMENSIONE = [10, 25, 50, 100];

const RICERCA_DEBOUNCE_MS = 400;

/**
 * Tab «Utenti» della pagina «Ruoli e permessi» (spec §10.4).
 *
 * Elencare tutti gli account del realm porta dentro anche gli ex dipendenti disattivati su
 * Keycloak: mostrarli come righe normali sarebbe peggio di non mostrarli, perché
 * l'amministratore assegnerebbe ruoli a persone che non possono più accedere. La colonna
 * «Stato» e il filtro sono ciò che rende leggibile la scelta «tutti gli account».
 *
 * Dal blocco 5 della specifica sul ciclo di vita lo stato non è più in sola lettura: con
 * `users.disable` si disattiva e si riattiva, con `users.delete` si elimina, con
 * `users.create` si crea e si rigenera la password. Le azioni che il permesso non concede
 * sono **assenti**, non disabilitate: un pulsante spento che non dice perché è peggio di un
 * pulsante che non c'è. Chi non ha nessuno dei tre vede esattamente la schermata di prima.
 *
 * Il caricamento si dichiara, non si mima: una tabella col solo header e «0 di 0» è
 * indistinguibile da un elenco vuoto — la stessa classe di difetto che questo blocco
 * presidia per l'errore, e il comportamento che `UserRolesPanel` ha già.
 *
 * Ricerca, stato e pagina li applica il backend (B14): scaricare tutto il realm per
 * mostrarne venticinque righe costava a ogni apertura del tab e a ogni chiusura del pannello
 * delle concessioni. È la stessa forma del tab Audit.
 */
export default function UsersTab({ onSelectUser, aggiornamento }) {
    const can = useCan();
    const canCreate = can('users.create');
    const canDisable = can('users.disable');
    const canDelete = can('users.delete');
    // La pulizia degli account cancellati revoca concessioni: stesso permesso della revoca.
    const canUpdate = can('users.update');

    const [users, setUsers] = useState([]);
    const [totale, setTotale] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('tutti');
    const [pagina, setPagina] = useState(0);
    const [dimensione, setDimensione] = useState(DIMENSIONE_PAGINA_DEFAULT);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    // Solo per evidenziare la riga aperta nel pannello: la selezione vera vive nel
    // chiamante, che decide se e quale pannello mostrare.
    const [selectedSub, setSelectedSub] = useState(null);
    // Bandiera propria: `error` sostituisce l'intera tabella, e il fallimento di un'azione
    // non deve far sparire un elenco caricato correttamente.
    const [actionError, setActionError] = useState(null);
    const [creaAperto, setCreaAperto] = useState(false);
    const [conferma, setConferma] = useState(null);
    const [passwordGenerata, setPasswordGenerata] = useState(null);
    const [puliziaAperta, setPuliziaAperta] = useState(false);
    const [puliziaInCorso, setPuliziaInCorso] = useState(false);
    const [esitoPulizia, setEsitoPulizia] = useState(null);

    const debounceRef = useRef(null);
    // Token di generazione, come in AuditTab: una risposta si scrive solo se e' ancora
    // quella dell'ultima richiesta. Con la ricerca sul backend due pagine possono tornare
    // fuori ordine, e vincerebbe quella vecchia.
    const generazioneRef = useRef(0);

    // `silenzioso`: la tabella resta al suo posto mentre si ricarica. Serve quando a
    // cambiare e' una riga sola, come dopo aver salvato le concessioni di una persona:
    // sostituirla con «Caricamento…» per un istante farebbe perdere il segno a chi legge.
    const loadUsers = useCallback(({ silenzioso = false } = {}) => {
        const generazione = ++generazioneRef.current;
        if (!silenzioso) {
            setLoading(true);
        }
        setError(false);
        userAdminService
            .listUsers({ q, stato: statusFilter, pagina, dimensione })
            .then((data) => {
                if (generazioneRef.current !== generazione) {
                    return;
                }
                const righe = (data && data.righe) || [];
                const conteggio = (data && data.totale) || 0;
                // Dopo un'eliminazione l'ultima pagina puo' restare vuota: si torna
                // all'ultima che ha righe, invece di mostrare «Nessun utente» con altri
                // utenti che esistono.
                if (righe.length === 0 && pagina > 0 && conteggio > 0) {
                    setPagina(Math.max(0, Math.ceil(conteggio / dimensione) - 1));
                    return;
                }
                setUsers(righe);
                setTotale(conteggio);
                setLoading(false);
            })
            .catch(() => {
                if (generazioneRef.current !== generazione) {
                    return;
                }
                setError(true);
                setLoading(false);
            });
    }, [q, statusFilter, pagina, dimensione]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    useEffect(
        () => () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        },
        [],
    );

    // Il chiamante lo incrementa quando qualcosa fuori da questa tabella ha cambiato un
    // utente — il pannello delle concessioni, alla chiusura dopo un salvataggio. Senza, la
    // riga mostrerebbe i ruoli di prima fino a un ricaricamento della pagina. Si rilegge la
    // sola pagina corrente, non il realm.
    const loadUsersRef = useRef(loadUsers);
    loadUsersRef.current = loadUsers;
    useEffect(() => {
        if (aggiornamento) {
            loadUsersRef.current({ silenzioso: true });
        }
    }, [aggiornamento]);

    const handleRicerca = (valore) => {
        setSearchInput(valore);
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            setQ(valore.trim());
            // Una ricerca nuova riparte dalla prima pagina: restare su una pagina alta
            // mostrerebbe un elenco vuoto che sembra «nessun risultato».
            setPagina(0);
        }, RICERCA_DEBOUNCE_MS);
    };

    const handleStato = (valore) => {
        setStatusFilter(valore);
        setPagina(0);
        setEsitoPulizia(null);
    };

    // Il pulsante compare solo a ricerca vuota: allora `totale` e' esattamente il numero di
    // persone che la pulizia tocchera' — con una ricerca attiva l'elenco ne mostrerebbe meno,
    // ma la pulizia le prenderebbe tutte.
    const puoRipulire = canUpdate && statusFilter === 'senza-account' && !q && !loading && totale > 0;

    const handlePulizia = () => {
        setPuliziaInCorso(true);
        setActionError(null);
        userAdminService.cleanupDeletedAccounts().then(
            (esito) => {
                setPuliziaInCorso(false);
                setPuliziaAperta(false);
                setEsitoPulizia(esito);
                loadUsers();
            },
            (err) => {
                setPuliziaInCorso(false);
                setPuliziaAperta(false);
                setActionError(
                    err?.response?.data?.message ||
                        'Non è stato possibile completare la pulizia. Le concessioni già ' +
                            'revocate restano revocate: si può rilanciarla.',
                );
            },
        );
    };

    // Un'azione sola per tutte: il verbo cambia, la forma no — esegui, poi ricarica, e in
    // caso di rifiuto mostra il messaggio del backend, che e' quello che dice *quale*
    // condizione manca (409 dell'invariante «resta un amministratore», 409 per un'azione su
    // se' stessi, 422 per una federazione in sola lettura).
    const esegui = (azione, user, fallback) => {
        setActionError(null);
        setConferma(null);
        return azione(user.sub).then(
            (esito) => {
                loadUsers();
                return esito;
            },
            (err) => {
                setActionError(err?.response?.data?.message || fallback);
            },
        );
    };

    const handleCreate = (nuovo) =>
        userAdminService.createUser(nuovo).then((esito) => {
            setCreaAperto(false);
            setPasswordGenerata(esito);
            loadUsers();
            return esito;
        });

    /**
     * Le azioni offerte su una riga: **solo quelle percorribili**, mai voci spente.
     *
     * Un permesso mancante toglie la voce invece di disabilitarla (§9): un comando spento
     * senza spiegazione fa credere a un guasto, e la spiegazione — «ti manca `users.delete`»
     * — non e' una frase da tooltip su ogni riga. Chi non ha nessun permesso non vede
     * alcun comando.
     *
     * <b>Non si prova a indovinare la federazione.</b> Se lo storage e' in sola lettura solo
     * il backend lo sa con certezza, e risponde 422 col suo messaggio: qui l'azione resta
     * offerta e il rifiuto si mostra. Nascondere in base a un'ipotesi sbagliata toglierebbe
     * un comando legittimo senza dire perche'.
     */
    const azioniDisponibili = (user) => {
        if (user.accountPresent === false) {
            return [];
        }
        const azioni = [];
        if (canCreate) {
            azioni.push({
                chiave: 'reset',
                etichetta: 'Rigenera password',
                icona: <ResetPasswordIcon fontSize="small" />,
                conferma: {
                    titolo: 'Rigenerare la password?',
                    testo: (u) =>
                        `La password attuale di ${u.displayName} smette di funzionare subito. ` +
                        'Quella nuova viene mostrata una volta sola e va consegnata a mano.',
                    conferma: 'Rigenera',
                },
                esegui: (u) =>
                    esegui(
                        userAdminService.resetPassword,
                        u,
                        'Non è stato possibile rigenerare la password.',
                    ).then((esito) => {
                        if (esito) {
                            setPasswordGenerata(esito);
                        }
                    }),
            });
        }
        if (canDisable) {
            azioni.push(
                user.enabled
                    ? {
                          chiave: 'disable',
                          etichetta: 'Disattiva',
                          icona: <BlockIcon fontSize="small" />,
                          conferma: {
                              titolo: 'Disattivare l’account?',
                              testo: (u) =>
                                  `${u.displayName} non potrà più accedere. È reversibile: ` +
                                  'riattivandolo ritrova i suoi ruoli, che restano assegnati. ' +
                                  NOTA_SESSIONI,
                              conferma: 'Disattiva',
                          },
                          esegui: (u) =>
                              esegui(
                                  userAdminService.disableUser,
                                  u,
                                  'Non è stato possibile disattivare l’account.',
                              ),
                      }
                    : {
                          chiave: 'enable',
                          etichetta: 'Riattiva',
                          icona: <EnableIcon fontSize="small" />,
                          // Riattivare non toglie niente a nessuno: chiedere conferma per
                          // un gesto reversibile e non distruttivo e' rumore.
                          esegui: (u) =>
                              esegui(
                                  userAdminService.enableUser,
                                  u,
                                  'Non è stato possibile riattivare l’account.',
                              ),
                      },
            );
        }
        if (canDelete) {
            azioni.push({
                chiave: 'delete',
                etichetta: 'Elimina',
                distruttiva: true,
                icona: <DeleteIcon fontSize="small" />,
                conferma: {
                    titolo: 'Eliminare l’utente?',
                    testo: (u) =>
                        `${u.displayName} perderà l’accesso, e le sue assegnazioni di ruolo e ` +
                        'di coda saranno rimosse. Il registro di audit conserva le sue azioni ' +
                        'passate. L’operazione non è reversibile: per una sospensione ' +
                        'temporanea usare Disattiva.',
                    conferma: 'Elimina',
                },
                esegui: (u) =>
                    esegui(
                        userAdminService.deleteUser,
                        u,
                        'Non è stato possibile eliminare l’utente.',
                    ),
            });
        }
        return azioni;
    };

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={() => loadUsers()}>
                            Riprova
                        </Button>
                    }
                >
                    Non è stato possibile caricare gli utenti.
                </Alert>
            </Box>
        );
    }

    const handleSelect = (sub) => {
        setSelectedSub(sub);
        onSelectUser(sub);
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {DESCRIZIONE_TAB}
                </Typography>
                {canCreate && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreaAperto(true)}
                        sx={{ flexShrink: 0 }}
                    >
                        Nuovo utente
                    </Button>
                )}
            </Box>

            {actionError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
                    {actionError}
                </Alert>
            )}

            {esitoPulizia && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEsitoPulizia(null)}>
                    {esitoPulizia.accounts === 0
                        ? 'Nessuna concessione da revocare: ogni persona con concessioni ha ancora un account.'
                        : `Revocate ${esitoPulizia.grants} ${esitoPulizia.grants === 1 ? 'concessione' : 'concessioni'} ` +
                          `a ${esitoPulizia.accounts} ${esitoPulizia.accounts === 1 ? 'persona' : 'persone'} senza account. ` +
                          'Ogni revoca è nel registro di audit.'}
                </Alert>
            )}

            {puoRipulire && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => setPuliziaAperta(true)}>
                            Revoca le loro concessioni
                        </Button>
                    }
                >
                    {totale === 1
                        ? 'Questa persona non ha più un account su Keycloak ma conserva delle concessioni.'
                        : `Queste ${totale} persone non hanno più un account su Keycloak ma conservano delle concessioni.`}{' '}
                    Non danno accesso a nessuno, ma falsano i titolari dei ruoli e i membri dei gruppi,
                    e impediscono di cancellare un ruolo assegnato solo a loro.
                </Alert>
            )}

            <BarraElenco
                ricerca={searchInput}
                onRicerca={handleRicerca}
                placeholderRicerca="Cerca per nome o email..."
                filtri={
                    <FiltroPillole
                        opzioni={FILTRI_STATO}
                        selezione={statusFilter}
                        onCambia={handleStato}
                        ariaLabel="Filtra per stato"
                    />
                }
                conteggio={
                    loading ? 'Caricamento…' : `${totale} ${totale === 1 ? 'utente' : 'utenti'}`
                }
            />

            <TableContainer {...CONTENITORE_TABELLA}>
                <Table size="small" sx={TABELLA_PAGINA}>
                    <TableHead>
                        <TableRow sx={TESTATA_TABELLA}>
                            <TableCell>Utente</TableCell>
                            <TableCell>Stato</TableCell>
                            <TableCell>Ruoli</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        Caricamento…
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && users.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nessun utente da mostrare.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading &&
                            users.map((user) => (
                                <TableRow
                                    key={user.sub}
                                    hover
                                    onClick={() => handleSelect(user.sub)}
                                    selected={user.sub === selectedSub}
                                    sx={{
                                        cursor: 'pointer',
                                        '&.Mui-selected': { bgcolor: 'primary.light' },
                                        '&.Mui-selected:hover': { bgcolor: 'primary.light' },
                                    }}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    bgcolor: 'primary.light',
                                                    color: 'primary.main',
                                                    fontSize: '0.8rem',
                                                }}
                                            >
                                                {iniziali(user.displayName)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2">{user.displayName}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {user.email}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            data-testid={`stato-${user.sub}`}
                                            label={statoDi(user).etichetta}
                                            // Gli sfondi vengono da `palette.stato` (v. theme.js):
                                            // `success.light` dei default MUI è un verde pieno, e a
                                            // sfondo dava una pastiglia satura invece della tinta
                                            // tenue del mockup; `action.hover` è nero al 4%, quasi
                                            // invisibile. I colori del testo sono già quelli giusti.
                                            sx={{
                                                bgcolor: user.enabled ? 'stato.attivoSfondo' : 'stato.inattivoSfondo',
                                                color: user.enabled ? 'success.main' : 'text.secondary',
                                                fontWeight: 400,
                                            }}
                                        />
                                        {statoDi(user).chiave === 'assente' && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block', mt: 0.5 }}
                                            >
                                                cancellato da Keycloak, restano le assegnazioni
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {user.roles && user.roles.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {user.roles.map((role) => (
                                                    <Chip
                                                        key={role.roleId}
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        label={`${role.label} · ${etichettaGruppi(role.groups)}`}
                                                        title={role.code || undefined}
                                                    />
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Nessun ruolo
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {/* Su chi non esiste piu' sul realm non c'e' niente da
                                            disattivare, riattivare o cancellare: le azioni
                                            fallirebbero con 404. Si ripulisce dal suo pannello,
                                            togliendo le assegnazioni rimaste. */}
                                        {/* I comandi in riga e non dietro un menu: e' la
                                            forma delle altre sezioni, e qui sono al massimo
                                            tre. Un menu chiede un clic per scoprire cosa
                                            c'e' dentro, e su un elenco da leggere a colpo
                                            d'occhio quel clic si paga su ogni riga. */}
                                        <Stack direction="row" spacing={1}
                                               justifyContent="flex-end">
                                            {azioniDisponibili(user).map((azione) => (
                                                <IconButton
                                                    key={azione.chiave}
                                                    size="small"
                                                    color={azione.distruttiva ? 'error' : undefined}
                                                    title={azione.etichetta}
                                                    aria-label={`${azione.etichetta} ${user.displayName}`}
                                                    onClick={(event) => {
                                                        // La riga intera apre il pannello delle
                                                        // concessioni: senza fermare il clic qui,
                                                        // «Elimina» aprirebbe anche quello, dietro
                                                        // la conferma.
                                                        event.stopPropagation();
                                                        if (azione.conferma) {
                                                            setConferma({ user, azione });
                                                        } else {
                                                            azione.esegui(user);
                                                        }
                                                    }}
                                                >
                                                    {azione.icona}
                                                </IconButton>
                                            ))}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totale}
                    page={pagina}
                    onPageChange={(event, nuova) => setPagina(nuova)}
                    rowsPerPage={dimensione}
                    onRowsPerPageChange={(event) => {
                        setDimensione(parseInt(event.target.value, 10));
                        setPagina(0);
                    }}
                    rowsPerPageOptions={OPZIONI_DIMENSIONE}
                    labelRowsPerPage="Righe per pagina"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
                />
            </TableContainer>


            <NewUserDialog
                open={creaAperto}
                onClose={() => setCreaAperto(false)}
                onCreate={handleCreate}
            />

            <PasswordTemporaneaDialog
                esito={passwordGenerata}
                onClose={() => setPasswordGenerata(null)}
            />

            <Dialog open={puliziaAperta} onClose={() => !puliziaInCorso && setPuliziaAperta(false)}>
                <DialogTitle>Revocare le concessioni degli account cancellati?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`Si revocano tutte le concessioni di ${totale === 1 ? 'una persona' : `${totale} persone`} ` +
                            'che non esistono più su Keycloak. Nessun account viene cancellato: non ' +
                            'esistono già più. Ogni revoca resta nel registro di audit. Se una di queste persone ' +
                            'venisse ricreata su Keycloak, avrebbe un nuovo identificativo e andrebbe ' +
                            'riassegnata comunque.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPuliziaAperta(false)} disabled={puliziaInCorso}>
                        Annulla
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handlePulizia}
                        disabled={puliziaInCorso}
                    >
                        {puliziaInCorso ? 'Revoca in corso…' : 'Revoca'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(conferma)} onClose={() => setConferma(null)}>
                <DialogTitle>{conferma?.azione.conferma.titolo}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {conferma && conferma.azione.conferma.testo(conferma.user)}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConferma(null)}>Annulla</Button>
                    <Button
                        color={conferma?.azione.distruttiva ? 'error' : 'primary'}
                        variant="contained"
                        onClick={() => conferma.azione.esegui(conferma.user)}
                    >
                        {conferma?.azione.conferma.conferma}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
